import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';

import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';

import { maskEpi, toMajorUnits } from '../bluesage.util';

import { BlueSageTerminalPushActions } from './bluesage-terminal-push.actions';
import { blueSageTerminalPushFeature } from './bluesage-terminal-push.feature';
import {
    BlueSagePushPhase,
    PUSH_CEILING_SECONDS,
    TIP_PROMPT_SECONDS,
} from './bluesage-terminal-push.state';

/**
 * The push status panel.
 *
 * A push is asynchronous with a 330-second ceiling, so every state here gets
 * plain language — **no bare spinners, and every wait state says what is being
 * waited on.** "Sending" and "waiting for the customer" are different things:
 * one is our latency, the other is the customer's, and an operator coaching
 * someone down the phone needs to know which they are in.
 *
 * Two pieces of copy are load-bearing:
 *
 *  - **A settled `auth` never says "Paid".** An authorization is money HELD.
 *    Saying paid would put money on the job the merchant has not received.
 *  - **A timed-out tip prompt never says the customer declined the tip.** We
 *    cannot know: a timed-out tip screen and a genuinely declined tip produce
 *    byte-identical payloads. It says *no tip recorded*, which is true either
 *    way.
 */
@Component({
    selector: 'app-bluesage-terminal-push',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        ProgressBarModule,
    ],
    providers: [ CurrencyPipe ],
    templateUrl: './bluesage-terminal-push.component.html',
    styleUrls: [ './bluesage-terminal-push.component.scss' ],
})
export class BlueSageTerminalPushComponent {

    actions = BlueSageTerminalPushActions;

    viewModel$ = this.store.select(createSelector({
        phase: blueSageTerminalPushFeature.selectPhase,

        /** `••••7943`, the digits printed on the device. */
        device: createSelector(
            blueSageTerminalPushFeature.selectEpi,
            maskEpi,
        ),

        /** Major units for display. Money crosses this boundary exactly once. */
        amount: createSelector(blueSageTerminalPushFeature.selectAmountMinor, toMajorUnits),

        elapsedSeconds: blueSageTerminalPushFeature.selectElapsedSeconds,

        /**
         * The tip countdown, shown only while COMPLETING a preauth.
         *
         * The completion tip screen gives roughly 30 seconds and is not
         * settable — a parameter download was tried and did not change it. A
         * live countdown lets the operator coach rather than guess.
         */
        tipSecondsLeft: createSelector(
            blueSageTerminalPushFeature.selectKind,
            blueSageTerminalPushFeature.selectPhase,
            blueSageTerminalPushFeature.selectElapsedSeconds,
            (kind, phase, elapsed) => kind === 'ticket' && phase === 'waiting'
                ? Math.max(0, TIP_PROMPT_SECONDS - elapsed)
                : undefined,
        ),

        /** How close we are to the point the device gives up on its own. */
        ceilingProgress: createSelector(
            blueSageTerminalPushFeature.selectElapsedSeconds,
            blueSageTerminalPushFeature.selectPhase,
            (elapsed, phase) => phase === 'waiting'
                ? Math.min(100, Math.round((elapsed / PUSH_CEILING_SECONDS) * 100))
                : 0,
        ),

        message: blueSageTerminalPushFeature.selectMessage,
        cancelling: blueSageTerminalPushFeature.selectCancelling,
        /** What was pushed — a cancelled COMPLETION leaves the hold open, and the copy says so. */
        kind: blueSageTerminalPushFeature.selectKind,
        transactionDiscarded: blueSageTerminalPushFeature.selectTransactionDiscarded,

        /**
         * The push is over, whatever the outcome.
         *
         * This is what puts a way OUT of the panel on screen. Without it the
         * dialog had no control at all once a push finished — the form is
         * replaced by the panel, and the panel's only buttons were Cancel (while
         * waiting) and Try again (on a failure). A cancelled or settled push
         * therefore left a dialog with nothing to press, which is exactly what
         * YMM hit on 2026-08-28: "the dialogue is stuck on cancelled — nothing
         * was charged, no way to get back to start of flow".
         */
        finished: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            (phase: BlueSagePushPhase) => phase === 'settled'
                || phase === 'authorized'
                || phase === 'declined'
                || phase === 'cancelled'
                || phase === 'timedOut'
                || phase === 'lost'
                || phase === 'failed',
        ),

        /**
         * Nothing is known about the money. The one place it will surface if the
         * customer completed on the device is the review queue, so the panel
         * offers the way there rather than leaving the operator to guess.
         */
        showUnmatchedLink: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            (phase: BlueSagePushPhase) => phase === 'timedOut' || phase === 'lost',
        ),

        /** Cancel is only meaningful while the device still has it. */
        showCancel: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            blueSageTerminalPushFeature.selectPushId,
            (phase, pushId) => phase === 'waiting' && Boolean(pushId),
        ),

        /**
         * Retry is offered on every outcome where nothing was taken.
         *
         * Not on `settled` or `authorized` — the money moved or is held, and a
         * "try again" beside a completed payment is an invitation to charge a
         * customer twice.
         */
        showTryAgain: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            blueSageTerminalPushFeature.selectTransactionDiscarded,
            // Nor once the transaction has been discarded: a retry replays a push
            // against a transaction id, and that row no longer exists. Going back
            // to the form and sending again is the only honest offer.
            // ⚠ Not on `timedOut` or `lost` either, any more. Nothing is KNOWN on
            // those: the customer may have completed on the device after the
            // middleware stopped waiting (live, 2026-09-03), and a retry then is a
            // second charge. The offer is "check the device and the queue", and
            // the way back to a fresh send is Done.
            (phase, discarded) => !discarded
                && (phase === 'declined' || phase === 'failed'),
        ),

        /** Anything on screen at all. */
        active: createSelector(
            blueSageTerminalPushFeature.selectPhase,
            (phase: BlueSagePushPhase) => phase !== 'idle',
        ),
    }));

    constructor(
        public store: Store,
    ) {}
}
